// A trailing-edge debounce for the moveend -> refetch path.
//
// The map fires moveend on every pan/zoom settle; refetching on each would
// hammer the rate-limited public API (F-063). We coalesce a burst of moves into
// one fetch after the map goes quiet for [delayMs].

/** A debounced function with a [cancel] to drop a pending call (e.g. on teardown). */
export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  cancel(): void;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, delayMs: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: A): void => {
    if (timer != null) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, delayMs);
  };
  debounced.cancel = (): void => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return debounced;
}
